import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { pageRange } from '@/lib/list-query'
import {
  buildTransitionPatch,
  localDateISO,
  type PipelineStage,
  type PipelineStagePatch,
  type TransitionInput,
} from '@/lib/pipeline'
import type { Database } from '@/types/database'

type PipelineInsert = Database['public']['Tables']['pipeline']['Insert']

const PIPELINE_KEY = ['pipeline'] as const

// Only what the list/board render — not select('*'). The account embeds need
// FK hints because pipeline has three FKs to accounts (reseller / distributor
// / end_user); rep needs one because assigned_to and solutions_architect_id
// both reference users.
// (One literal string: supabase-js parses it at the type level, and string
// concatenation widens to `string`, losing row typing.)
const PIPELINE_LIST_COLUMNS =
  'id, stage, quote_ref, total_amount, target_close_date, expiration_date, goal, source, updated_at, reseller:accounts!reseller_account_id(id,name), end_user:accounts!end_user_account_id(id,name), division:account_divisions!reseller_division_id(id,name), rep:users!pipeline_assigned_to_fkey(id,full_name)'

// Whitelist of server-sort keys → PostgREST order columns; '*_name' keys
// order parent rows by the embedded relation's name. 'stage' follows the
// enum's declaration order (pre_quote first).
const PIPELINE_SORT_COLUMNS: Record<string, string> = {
  updated_at: 'updated_at',
  total_amount: 'total_amount',
  target_close_date: 'target_close_date',
  expiration_date: 'expiration_date',
  quote_ref: 'quote_ref',
  stage: 'stage',
  reseller_name: 'reseller(name)',
  end_user_name: 'end_user(name)',
  rep_name: 'rep(full_name)',
}

export interface PipelineListParams {
  pageIndex: number
  pageSize: number
  sortBy: string
  sortDir: 'asc' | 'desc'
  /** Server-side stage filter; omit for all stages. */
  stages?: readonly PipelineStage[]
}

export function usePipelineList(params: PipelineListParams) {
  return useQuery({
    queryKey: [...PIPELINE_KEY, 'list', params],
    queryFn: async () => {
      const { from, to } = pageRange(params.pageIndex, params.pageSize)
      let query = supabase
        .from('pipeline')
        .select(PIPELINE_LIST_COLUMNS, { count: 'exact' })
      if (params.stages?.length) query = query.in('stage', [...params.stages])
      const sortBy = PIPELINE_SORT_COLUMNS[params.sortBy] ?? 'updated_at'
      const { data, error, count } = await query
        .order(sortBy, { ascending: params.sortDir === 'asc' })
        // stable tie-break so rows can't straddle page boundaries
        .order('id', { ascending: true })
        .range(from, to)
      if (error) throw error
      return { rows: data, total: count ?? 0 }
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

// Everything the detail header + Overview tab render, embeds included; same
// one-literal-string rule as the list columns.
const PIPELINE_DETAIL_COLUMNS =
  'id, stage, quote_ref, ref_number, reseller_cust_id, quote_type, quote_source, goal, source, subtotal, freight_amount, total_amount, create_date, submit_date, target_quote_date, target_close_date, expiration_date, actual_close_date, revisit_date, on_hold_reason, loss_reason_id, loss_notes, notes, product_categories, created_at, updated_at, reseller:accounts!reseller_account_id(id,name), end_user:accounts!end_user_account_id(id,name), distributor:accounts!distributor_account_id(id,name), division:account_divisions!reseller_division_id(id,name), rep:users!pipeline_assigned_to_fkey(id,full_name), solutions_architect:users!pipeline_solutions_architect_id_fkey(id,full_name), reseller_contact:contacts!pipeline_reseller_contact_id_fkey(id,first_name,last_name), loss_reason:tenant_loss_reasons!pipeline_loss_reason_tenant_fkey(id,key,label)'

export function usePipelineDeal(id: string) {
  return useQuery({
    queryKey: [...PIPELINE_KEY, 'detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pipeline')
        .select(PIPELINE_DETAIL_COLUMNS)
        .eq('id', id)
        .single()
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
}

// The tenant's own selectable loss reasons (migration 034 + the R5 default
// trigger). RLS (tenant_guard) scopes this to the current tenant; is_active
// filters out reasons an admin retired, and sort_order fixes the dropdown
// order. Historical deals still render a retired reason via the detail embed —
// this list only governs what a NEW loss transition may pick.
const TENANT_LOSS_REASON_COLUMNS = 'id, key, label, is_active, sort_order'

export function useTenantLossReasons(enabled = true) {
  return useQuery({
    queryKey: [...PIPELINE_KEY, 'loss-reasons'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_loss_reasons')
        .select(TENANT_LOSS_REASON_COLUMNS)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
      if (error) throw error
      return data
    },
    enabled,
    staleTime: 5 * 60_000,
  })
}

// Quick Pre-Quote create. Errors surface inline in the form (contract: no
// error toasts); success shows as navigation to the new deal, so no toast
// either. The caller builds the payload via buildDealInsert.
export function useCreatePipelineDeal() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: [...PIPELINE_KEY, 'create'],
    mutationFn: async (values: PipelineInsert) => {
      const { data, error } = await supabase
        .from('pipeline')
        .insert(values)
        .select('id')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: PIPELINE_KEY }),
  })
}

export interface StageTransitionVars {
  id: string
  from: PipelineStage
  to: PipelineStage
  input?: TransitionInput
}

// Minimal structural shape of cached list pages; the real rows carry more
// fields, which spread-updates below preserve untouched.
export interface CachedPipelineList {
  rows: { id: string; stage: PipelineStage }[]
  total: number
}

export interface StageTransitionContext {
  previous: [QueryKey, CachedPipelineList | undefined][]
  detailKey: QueryKey
  previousDetail: { stage: PipelineStage } | undefined
}

// The optimistic cache surgery for a stage move, extracted from the hook's
// onMutate so it is unit-testable against a bare QueryClient (node env, no
// renderHook). The full patch (stage plus whatever the move sets or clears —
// close date, loss fields) lands optimistically: every cached list page flips
// the row; pages whose stage filter no longer matches drop it (card leaves
// its board column immediately) and the target stage's first page gains it
// (card lands in the new column immediately — the settle refetch re-sorts it
// into place). The detail view gets the same patch, so a reopen's cleared
// fields disappear from the header without waiting on the refetch.
export function applyOptimisticStageMove(
  qc: QueryClient,
  id: string,
  to: PipelineStage,
  patch: PipelineStagePatch,
): StageTransitionContext {
  const previous = qc.getQueriesData<CachedPipelineList>({
    queryKey: [...PIPELINE_KEY, 'list'],
  })
  const detailKey = [...PIPELINE_KEY, 'detail', id]
  const previousDetail = qc.getQueryData<{ stage: PipelineStage }>(detailKey)
  if (previousDetail) qc.setQueryData(detailKey, { ...previousDetail, ...patch })
  const moved = previous
    .flatMap(([, data]) => data?.rows ?? [])
    .find((row) => row.id === id)
  for (const [key, data] of previous) {
    if (!data) continue
    const params = key[2] as PipelineListParams | undefined
    const holds = data.rows.some((row) => row.id === id)
    if (holds) {
      const excluded = params?.stages && !params.stages.includes(to)
      qc.setQueryData<CachedPipelineList>(
        key,
        excluded
          ? { rows: data.rows.filter((row) => row.id !== id), total: data.total - 1 }
          : { ...data, rows: data.rows.map((row) => (row.id === id ? { ...row, ...patch } : row)) },
      )
    } else if (moved && params?.pageIndex === 0 && params.stages?.includes(to)) {
      qc.setQueryData<CachedPipelineList>(key, {
        rows: [{ ...moved, ...patch }, ...data.rows],
        total: data.total + 1,
      })
    }
  }
  return { previous, detailKey, previousDetail }
}

export function rollbackStageMove(qc: QueryClient, ctx: StageTransitionContext): void {
  for (const [key, data] of ctx.previous) qc.setQueryData(key, data)
  if (ctx.previousDetail) qc.setQueryData(ctx.detailKey, ctx.previousDetail)
}

/**
 * Validated stage move with optimistic cache updates. The patch is built by
 * the stage machine (buildTransitionPatch) so no caller can commit a move the
 * machine forbids or skip its required fields. The optimistic flip and its
 * rollback live in applyOptimisticStageMove/rollbackStageMove above. On
 * server error everything rolls back and a toast says why.
 */
export function usePipelineStageTransition() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, from, to, input = {} }: StageTransitionVars) => {
      const plan = buildTransitionPatch(from, to, input, localDateISO(new Date()))
      if (!plan.ok) {
        throw new Error(
          [plan.errors.revisitDate, plan.errors.lossReason].filter(Boolean).join(' '),
        )
      }
      const { error } = await supabase.from('pipeline').update(plan.patch).eq('id', id)
      if (error) throw error
    },
    onMutate: async ({ id, from, to, input = {} }) => {
      // Build the same plan the mutation will commit so the optimistic caches
      // carry the FULL patch (a reopen's cleared fields, not just the stage).
      // Invalid plans touch nothing — mutationFn throws the validation error
      // and onError has no context to roll back.
      const plan = buildTransitionPatch(from, to, input, localDateISO(new Date()))
      if (!plan.ok) return undefined
      await qc.cancelQueries({ queryKey: PIPELINE_KEY })
      return applyOptimisticStageMove(qc, id, to, plan.patch)
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx) rollbackStageMove(qc, ctx)
      toast.error(`Stage change failed: ${err.message}`)
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: PIPELINE_KEY }),
  })
}

// The funnel shell sums client-side over a capped window — never unbounded.
// TODO: real funnel viz (later) — replace this with a server-side aggregate
// (RPC or view, coordinated with Charlie) before volume approaches the cap;
// past the cap the sums shown are partial and the UI says so.
export const FUNNEL_ROW_CAP = 1000

export function usePipelineFunnel() {
  return useQuery({
    queryKey: [...PIPELINE_KEY, 'funnel'],
    queryFn: async () => {
      const { data, error, count } = await supabase
        .from('pipeline')
        .select('stage, total_amount', { count: 'exact' })
        .range(0, FUNNEL_ROW_CAP - 1)
      if (error) throw error
      const total = count ?? 0
      return { rows: data, total, capped: total > FUNNEL_ROW_CAP }
    },
    staleTime: 30_000,
  })
}

// Read-only check for the reopen dialog: a Won deal that already generated an
// AMS contract gets a non-blocking warning. head:true fetches only the count;
// contracts are never modified from the pipeline UI.
export function usePipelineHasAmsContract(pipelineId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...PIPELINE_KEY, 'has-ams-contract', pipelineId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('ams_contracts')
        .select('id', { count: 'exact', head: true })
        .eq('pipeline_id', pipelineId)
      if (error) throw error
      return (count ?? 0) > 0
    },
    enabled,
    staleTime: 30_000,
  })
}

// ——— Detail-tab reads. All deal-scoped and read-only this sprint; RLS walls
// (tenant + has_entity_access) apply through the normal client. ———

const LINE_ITEM_COLUMNS =
  'id, vendor_part_number, description, quantity, unit_price, ext_amount, sla_tier, contract_term, contract_start_date, contract_end_date, is_ncnr, is_tm, master_category, product_line'

export function usePipelineLineItems(pipelineId: string) {
  return useQuery({
    queryKey: [...PIPELINE_KEY, 'line-items', pipelineId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pipeline_line_items')
        .select(LINE_ITEM_COLUMNS)
        .eq('pipeline_id', pipelineId)
        .order('created_at', { ascending: true })
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
}

const ATTACHMENT_COLUMNS =
  'id, original_filename, file_type, notes, uploaded_at, uploader:users!attachments_uploaded_by_fkey(id,full_name)'

export function usePipelineAttachments(pipelineId: string) {
  return useQuery({
    queryKey: [...PIPELINE_KEY, 'attachments', pipelineId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('attachments')
        .select(ATTACHMENT_COLUMNS)
        .eq('entity_type', 'pipeline')
        .eq('entity_id', pipelineId)
        .order('uploaded_at', { ascending: false })
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
}

// Deal-scoped tasks (any assignee, any status) — distinct from use-tasks.ts's
// my-open-tasks list, which is user-scoped.
const DEAL_TASK_COLUMNS =
  'id, title, task_type, status, due_date, created_at, assignee:users!tasks_assigned_to_fkey(id,full_name)'

export function usePipelineTasks(pipelineId: string) {
  return useQuery({
    queryKey: [...PIPELINE_KEY, 'tasks', pipelineId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select(DEAL_TASK_COLUMNS)
        .eq('entity_type', 'pipeline')
        .eq('entity_id', pipelineId)
        .order('due_date', { ascending: true, nullsFirst: false })
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
}

const NOTE_COLUMNS =
  'id, content, created_at, updated_at, author:users!notes_created_by_fkey(id,full_name)'

export function usePipelineNotes(pipelineId: string) {
  return useQuery({
    queryKey: [...PIPELINE_KEY, 'notes', pipelineId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notes')
        .select(NOTE_COLUMNS)
        .eq('entity_type', 'pipeline')
        .eq('entity_id', pipelineId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
}

// audit_log SELECT is admin-only by RLS (audit_log_select policy); members
// get zero rows and the Activity tab's empty state says so.
const ACTIVITY_COLUMNS = 'id, action, actor_email, occurred_at, changes'
export const ACTIVITY_ROW_CAP = 50

export function usePipelineActivity(pipelineId: string) {
  return useQuery({
    queryKey: [...PIPELINE_KEY, 'activity', pipelineId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_log')
        .select(ACTIVITY_COLUMNS)
        .eq('entity_type', 'pipeline')
        .eq('entity_id', pipelineId)
        .order('occurred_at', { ascending: false })
        .limit(ACTIVITY_ROW_CAP)
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
}
